'use client'

import ConfirmModal from '@/components/confirm-modal'
import { UserSelector } from '@/components/UserSelector'
import { WalletSelector } from '@/components/WalletSelector'
import {
    addWalletMember,
    createWallet,
    createWalletsFromUsers,
    deleteWallet,
    getActiveWallet,
    getEligibleUsers,
    getWallet,
    getWalletClients,
    getWalletConfig,
    getWallets,
    removeWalletMember,
    SalesWallet,
    SalesWalletConfig,
    WalletClient,
    WalletEligibleUser,
    setActiveWallet,
    transferClients,
    updateWallet,
    updateWalletConfig,
    updateWalletMember,
    WALLET_DISTRIBUTION_MODE_HINTS,
    WALLET_DISTRIBUTION_MODE_LABELS,
    WalletDistributionMode,
} from '@/lib/api/sales'
import { getCompanies } from '@/lib/api/companies/companies-api'
import { Company } from '@/lib/api/common/interfaces/company.interface'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    MdAccountBalanceWallet,
    MdAdd,
    MdClose,
    MdDelete,
    MdEdit,
    MdExpandLess,
    MdExpandMore,
    MdGroups,
    MdMoveDown,
    MdPeople,
    MdPersonAdd,
    MdPersonRemove,
    MdSettings,
    MdStar,
    MdStarBorder,
    MdStore,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { formatPhoneNumber } from '@/lib/form-state/util'

export default function WalletsListPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('sales', 'wallets')

    const [loading, setLoading] = useState(true)
    const [wallets, setWallets] = useState<SalesWallet[]>([])
    const [expandedWalletId, setExpandedWalletId] = useState<number | null>(null)
    const [expandedWallet, setExpandedWallet] = useState<SalesWallet | null>(null)
    const [loadingDetail, setLoadingDetail] = useState(false)

    // Carteira ativa da sessão: onde caem os clientes que este usuário cadastrar.
    const [activeWalletId, setActiveWalletId] = useState<number | null>(null)
    const [settingActive, setSettingActive] = useState(false)

    // Create / Edit modal
    const [showModal, setShowModal] = useState(false)
    const [editingWallet, setEditingWallet] = useState<SalesWallet | null>(null)
    const [formName, setFormName] = useState('')
    const [formDescription, setFormDescription] = useState('')
    const [formAcceptsAutoAssign, setFormAcceptsAutoAssign] = useState(true)
    const [formWeight, setFormWeight] = useState('1')
    const [formMaxClients, setFormMaxClients] = useState('')
    const [formCompanyIds, setFormCompanyIds] = useState<number[]>([])
    const [companies, setCompanies] = useState<Company[]>([])
    const [submitting, setSubmitting] = useState(false)

    const toggleFormCompany = (companyId: number) => {
        setFormCompanyIds(prev => prev.includes(companyId)
            ? prev.filter(c => c !== companyId)
            : [...prev, companyId])
    }

    // Aba "em massa": uma carteira por usuário marcado, nomeada com o nome dele.
    const [modalTab, setModalTab] = useState<'single' | 'bulk'>('single')
    const [eligibleUsers, setEligibleUsers] = useState<WalletEligibleUser[]>([])
    const [loadingEligible, setLoadingEligible] = useState(false)
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
    const [userSearch, setUserSearch] = useState('')
    const [creatingBulk, setCreatingBulk] = useState(false)

    const filteredEligibleUsers = eligibleUsers.filter(u => {
        const term = userSearch.trim().toLowerCase()
        if (!term) return true
        return (
            u.name.toLowerCase().includes(term) ||
            (u.email || '').toLowerCase().includes(term) ||
            u.companyNames.some(c => c.toLowerCase().includes(term))
        )
    })

    const selectableUsers = filteredEligibleUsers.filter(u => !u.hasWallet)

    const toggleUser = (userId: number) => {
        setSelectedUserIds(prev => prev.includes(userId)
            ? prev.filter(id => id !== userId)
            : [...prev, userId])
    }

    const toggleAllUsers = () => {
        setSelectedUserIds(prev =>
            prev.length === selectableUsers.length ? [] : selectableUsers.map(u => u.id)
        )
    }

    const loadEligibleUsers = async () => {
        setLoadingEligible(true)
        try {
            const response = await getEligibleUsers()
            if (response.success) setEligibleUsers(response.result || [])
            else toast.error(response.error || 'Erro ao carregar usuários')
        } catch {
            toast.error('Erro ao carregar usuários')
        } finally {
            setLoadingEligible(false)
        }
    }

    const handleCreateBulk = async () => {
        if (selectedUserIds.length === 0) return

        setCreatingBulk(true)
        try {
            const response = await createWalletsFromUsers({
                userIds: selectedUserIds,
                // Filiais em branco: cada carteira herda as do próprio usuário.
                companyIds: formCompanyIds.length > 0 ? formCompanyIds : undefined,
                acceptsAutoAssign: formAcceptsAutoAssign,
                weight: parseInt(formWeight, 10) || 1,
                maxClients: formMaxClients ? parseInt(formMaxClients, 10) : null,
            })

            if (response.success && response.result) {
                const { created, skipped } = response.result
                toast.success(`${created.length} carteira(s) criada(s)`)
                if (skipped.length > 0) {
                    toast.info(
                        `${skipped.length} ignorado(s): ${skipped.map(s => s.name).join(', ')}`
                    )
                }
                setShowModal(false)
                setSelectedUserIds([])
                loadWallets()
            } else {
                toast.error(response.error || 'Erro ao criar carteiras')
            }
        } catch {
            toast.error('Erro ao criar carteiras')
        } finally {
            setCreatingBulk(false)
        }
    }

    // Modal de clientes da carteira: busca, seleção e transferência.
    const [clientsModalWallet, setClientsModalWallet] = useState<SalesWallet | null>(null)
    const [walletClients, setWalletClients] = useState<WalletClient[]>([])
    const [clientsTotal, setClientsTotal] = useState(0)
    const [clientsPage, setClientsPage] = useState(1)
    const [clientsSearch, setClientsSearch] = useState('')
    const [loadingClients, setLoadingClients] = useState(false)
    const [selectedClientIds, setSelectedClientIds] = useState<number[]>([])
    const [targetWalletId, setTargetWalletId] = useState<number | null>(null)
    // Levar as oportunidades abertas para o funil da nova dona. Marcado por
    // padrão: é o que a transferência fazia antes de a escolha existir.
    const [moveClientDeals, setMoveClientDeals] = useState(true)
    const [movingClients, setMovingClients] = useState(false)

    const CLIENTS_PAGE_SIZE = 25
    const clientsTotalPages = Math.max(1, Math.ceil(clientsTotal / CLIENTS_PAGE_SIZE))

    const openClientsModal = (wallet: SalesWallet) => {
        setClientsModalWallet(wallet)
        setWalletClients([])
        setClientsSearch('')
        setClientsPage(1)
        setSelectedClientIds([])
        setTargetWalletId(null)
    }

    // Busca com debounce: cada tecla dispararia uma consulta à base inteira.
    useEffect(() => {
        if (!clientsModalWallet) return
        const timer = setTimeout(() => loadWalletClients(), 350)
        return () => clearTimeout(timer)
    }, [clientsModalWallet?.id, clientsSearch, clientsPage])

    const loadWalletClients = async () => {
        if (!clientsModalWallet) return
        setLoadingClients(true)
        try {
            const response = await getWalletClients(clientsModalWallet.id, {
                search: clientsSearch.trim() || undefined,
                page: clientsPage,
                limit: CLIENTS_PAGE_SIZE,
            })
            if (response.success && response.result) {
                setWalletClients(response.result.items || [])
                setClientsTotal(response.result.total || 0)
            } else {
                toast.error(response.error || 'Erro ao carregar clientes')
            }
        } catch {
            toast.error('Erro ao carregar clientes')
        } finally {
            setLoadingClients(false)
        }
    }

    const toggleClient = (clientId: number) => {
        setSelectedClientIds(prev => prev.includes(clientId)
            ? prev.filter(id => id !== clientId)
            : [...prev, clientId])
    }

    const toggleAllClients = () => {
        setSelectedClientIds(prev =>
            prev.length === walletClients.length ? [] : walletClients.map(c => c.id)
        )
    }

    const handleMoveClients = async () => {
        if (selectedClientIds.length === 0 || !targetWalletId) return

        setMovingClients(true)
        try {
            const response = await transferClients(
                selectedClientIds,
                targetWalletId,
                moveClientDeals,
            )
            if (response.success) {
                toast.success(`${response.result?.moved ?? selectedClientIds.length} cliente(s) movidos`)
                setSelectedClientIds([])
                setTargetWalletId(null)
                loadWalletClients()
                loadWallets()
            } else {
                toast.error(response.error || 'Erro ao mover clientes')
            }
        } catch {
            toast.error('Erro ao mover clientes')
        } finally {
            setMovingClients(false)
        }
    }

    // Configuração de distribuição (organização)
    const [showConfigModal, setShowConfigModal] = useState(false)
    const [config, setConfig] = useState<SalesWalletConfig | null>(null)
    const [savingConfig, setSavingConfig] = useState(false)

    // Delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [walletToDelete, setWalletToDelete] = useState<SalesWallet | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Add member
    const [showAddMemberForm, setShowAddMemberForm] = useState(false)
    const [memberUserId, setMemberUserId] = useState<number | null>(null)
    const [addingMember, setAddingMember] = useState(false)

    // Remove member confirmation
    const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false)
    const [memberToRemove, setMemberToRemove] = useState<{ walletId: number; userId: number; userName: string } | null>(null)
    const [removingMember, setRemovingMember] = useState(false)

    // Carteiras são globais por organização — não recarrega ao trocar de filial.
    useEffect(() => {
        if (user?.organizationId) {
            loadWallets()
            loadConfig()
            loadActiveWallet()
            loadCompanies()
        }
    }, [user?.organizationId])

    const loadCompanies = async () => {
        try {
            const response = await getCompanies()
            if (!response.success) return
            // O endpoint devolve as empresas de todas as organizações; a
            // carteira só pode apontar para filiais da própria organização.
            setCompanies(
                (response.result || []).filter(
                    c => c.organizationId === user?.organizationId
                )
            )
        } catch {
            // sem filiais o campo some do formulário — silencia
        }
    }

    const loadActiveWallet = async () => {
        try {
            const response = await getActiveWallet()
            if (response.success && response.result) {
                setActiveWalletId(response.result.activeSalesWalletId)
            }
        } catch {
            // sem carteira ativa é estado válido — silencia
        }
    }

    const loadWallets = async () => {
        setLoading(true)
        try {
            const response = await getWallets()
            if (response.success) {
                setWallets(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar carteiras')
            }
        } catch {
            toast.error('Erro ao carregar carteiras')
        } finally {
            setLoading(false)
        }
    }

    const loadConfig = async () => {
        try {
            const response = await getWalletConfig()
            if (response.success && response.result) {
                setConfig(response.result)
            }
        } catch {
            // configuração é opcional na tela — silencia
        }
    }

    const loadWalletDetail = async (id: number) => {
        setLoadingDetail(true)
        try {
            const response = await getWallet(id)
            if (response.success) {
                setExpandedWallet(response.result)
            }
        } catch {
            toast.error('Erro ao carregar detalhes da carteira')
        } finally {
            setLoadingDetail(false)
        }
    }

    const handleToggleExpand = (wallet: SalesWallet) => {
        if (expandedWalletId === wallet.id) {
            setExpandedWalletId(null)
            setExpandedWallet(null)
            setShowAddMemberForm(false)
        } else {
            setExpandedWalletId(wallet.id)
            loadWalletDetail(wallet.id)
        }
    }

    const openCreateModal = () => {
        setEditingWallet(null)
        setFormName('')
        setFormDescription('')
        setFormAcceptsAutoAssign(true)
        setFormWeight('1')
        setFormMaxClients('')
        setFormCompanyIds([])
        setModalTab('single')
        setSelectedUserIds([])
        setUserSearch('')
        setShowModal(true)
        if (eligibleUsers.length === 0) loadEligibleUsers()
    }

    const openEditModal = (wallet: SalesWallet) => {
        setEditingWallet(wallet)
        setFormName(wallet.name)
        setFormDescription(wallet.description || '')
        setFormAcceptsAutoAssign(wallet.acceptsAutoAssign ?? true)
        setFormWeight(String(wallet.weight ?? 1))
        setFormMaxClients(wallet.maxClients != null ? String(wallet.maxClients) : '')
        setFormCompanyIds(wallet.companyIds || [])
        setModalTab('single')
        setShowModal(true)
    }

    const handleSubmit = async () => {
        if (!formName.trim()) {
            toast.error('O nome da carteira é obrigatório')
            return
        }

        const payload = {
            name: formName.trim(),
            description: formDescription.trim() || undefined,
            companyIds: formCompanyIds,
            acceptsAutoAssign: formAcceptsAutoAssign,
            weight: parseInt(formWeight, 10) || 1,
            maxClients: formMaxClients ? parseInt(formMaxClients, 10) : null,
        }

        setSubmitting(true)
        try {
            if (editingWallet) {
                const response = await updateWallet(editingWallet.id, payload)
                if (response.success) {
                    toast.success('Carteira atualizada com sucesso')
                    setShowModal(false)
                    loadWallets()
                    if (expandedWalletId === editingWallet.id) {
                        loadWalletDetail(editingWallet.id)
                    }
                } else {
                    toast.error(response.error || 'Erro ao atualizar carteira')
                }
            } else {
                const response = await createWallet(payload)
                if (response.success) {
                    toast.success('Carteira criada com sucesso')
                    setShowModal(false)
                    loadWallets()
                } else {
                    toast.error(response.error || 'Erro ao criar carteira')
                }
            }
        } catch {
            toast.error('Erro ao salvar carteira')
        } finally {
            setSubmitting(false)
        }
    }

    const handleSaveConfig = async () => {
        if (!config) return
        setSavingConfig(true)
        try {
            const response = await updateWalletConfig({
                autoAssignEnabled: config.autoAssignEnabled,
                distributionMode: config.distributionMode,
                defaultWalletId: config.defaultWalletId ?? null,
                prospectDiscardEnabled: config.prospectDiscardEnabled,
                prospectRetentionDays: config.prospectRetentionDays ?? null,
            })
            if (response.success) {
                toast.success('Configuração salva com sucesso')
                setShowConfigModal(false)
                if (response.result) setConfig(response.result)
            } else {
                toast.error(response.error || 'Erro ao salvar configuração')
            }
        } catch {
            toast.error('Erro ao salvar configuração')
        } finally {
            setSavingConfig(false)
        }
    }

    const handleSetActive = async (walletId: number) => {
        setSettingActive(true)
        try {
            const next = activeWalletId === walletId ? null : walletId
            const response = await setActiveWallet(next)
            if (response.success) {
                setActiveWalletId(next)
                toast.success(
                    next
                        ? 'Os clientes que você cadastrar entrarão nesta carteira'
                        : 'Carteira ativa removida'
                )
            } else {
                toast.error(response.error || 'Erro ao definir carteira ativa')
            }
        } catch {
            toast.error('Erro ao definir carteira ativa')
        } finally {
            setSettingActive(false)
        }
    }

    const handleDelete = (wallet: SalesWallet) => {
        setWalletToDelete(wallet)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!walletToDelete) return
        setDeleting(true)
        try {
            const response = await deleteWallet(walletToDelete.id)
            if (response.success) {
                const released = response.result?.releasedClients ?? 0
                toast.success(
                    released > 0
                        ? `Carteira excluída. ${released} cliente(s) voltaram ao pool.`
                        : 'Carteira excluída com sucesso'
                )
                setShowDeleteModal(false)
                setWalletToDelete(null)
                if (expandedWalletId === walletToDelete.id) {
                    setExpandedWalletId(null)
                    setExpandedWallet(null)
                }
                loadWallets()
            } else {
                toast.error(response.error || 'Erro ao excluir carteira')
            }
        } catch {
            toast.error('Erro ao excluir carteira')
        } finally {
            setDeleting(false)
        }
    }

    const handleAddMember = async () => {
        if (!memberUserId || !expandedWalletId) return

        setAddingMember(true)
        try {
            const response = await addWalletMember(expandedWalletId, {
                userId: memberUserId,
            })
            if (response.success) {
                toast.success('Membro adicionado com sucesso')
                setMemberUserId(null)
                setShowAddMemberForm(false)
                loadWalletDetail(expandedWalletId)
                loadWallets()
            } else {
                toast.error(response.error || 'Erro ao adicionar membro')
            }
        } catch {
            toast.error('Erro ao adicionar membro')
        } finally {
            setAddingMember(false)
        }
    }

    const handleToggleMemberActive = async (walletId: number, userId: number, active: boolean) => {
        try {
            const response = await updateWalletMember(walletId, userId, { active })
            if (response.success) {
                toast.success(active ? 'Membro ativado' : 'Membro desativado')
                loadWalletDetail(walletId)
            } else {
                toast.error(response.error || 'Erro ao atualizar membro')
            }
        } catch {
            toast.error('Erro ao atualizar membro')
        }
    }

    const handleRemoveMember = (walletId: number, userId: number, userName: string) => {
        setMemberToRemove({ walletId, userId, userName })
        setShowRemoveMemberModal(true)
    }

    const confirmRemoveMember = async () => {
        if (!memberToRemove) return
        setRemovingMember(true)
        try {
            const response = await removeWalletMember(memberToRemove.walletId, memberToRemove.userId)
            if (response.success) {
                toast.success('Membro removido com sucesso')
                setShowRemoveMemberModal(false)
                setMemberToRemove(null)
                loadWalletDetail(memberToRemove.walletId)
                loadWallets()
            } else {
                toast.error(response.error || 'Erro ao remover membro')
            }
        } catch {
            toast.error('Erro ao remover membro')
        } finally {
            setRemovingMember(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col gap-6 p-6">
                <div className="flex items-center gap-3">
                    <div className="skeleton w-10 h-10 rounded-xl" />
                    <div>
                        <div className="skeleton h-6 w-48 mb-1" />
                        <div className="skeleton h-4 w-32" />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="card bg-base-100 shadow-sm">
                            <div className="card-body">
                                <div className="skeleton h-5 w-40 mb-2" />
                                <div className="skeleton h-4 w-full mb-3" />
                                <div className="flex gap-2">
                                    <div className="skeleton h-6 w-20" />
                                    <div className="skeleton h-6 w-16" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdAccountBalanceWallet className="text-2xl text-white" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-base-content">
                                Carteiras de Clientes
                            </h1>
                            <span className="badge badge-sm badge-secondary">
                                {wallets.length}
                            </span>
                        </div>
                        <p className="text-sm text-base-content/60">
                            O cliente pertence à carteira e é atendido por todos os membros dela
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        className="btn btn-ghost gap-2"
                        onClick={() => navigate('/sales/wallets/unassigned')}
                        title="Clientes sem carteira"
                    >
                        <MdGroups className="text-xl" />
                        <span className="hidden sm:inline">Sem carteira</span>
                    </button>
                    {canUpdate && config && (
                        <button
                            className="btn btn-ghost gap-2"
                            onClick={() => setShowConfigModal(true)}
                            title="Distribuição automática"
                        >
                            <MdSettings className="text-xl" />
                            <span className="hidden sm:inline">Distribuição</span>
                        </button>
                    )}
                    {canCreate && (
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={openCreateModal}
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Criar Carteira</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Empty State */}
            {wallets.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdAccountBalanceWallet className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma carteira cadastrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Crie carteiras para organizar a base de clientes entre as equipes
                        </p>
                        {canCreate && (
                            <button
                                onClick={openCreateModal}
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Criar primeira carteira
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    {wallets.map(wallet => (
                        <div
                            key={wallet.id}
                            className={
                                'card bg-base-100 shadow-sm border overflow-hidden ' +
                                (activeWalletId === wallet.id
                                    ? 'border-secondary'
                                    : 'border-base-300/50')
                            }
                        >
                            <div className="card-body p-4">
                                {/* Wallet Header */}
                                <div className="flex items-start justify-between">
                                    <div
                                        className="flex-1 cursor-pointer"
                                        onClick={() => handleToggleExpand(wallet)}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-bold text-base-content text-lg">
                                                {wallet.name}
                                            </h3>
                                            {activeWalletId === wallet.id && (
                                                <span className="badge badge-sm badge-secondary">
                                                    Minha carteira ativa
                                                </span>
                                            )}
                                            {!wallet.active && (
                                                <span className="badge badge-sm badge-error badge-outline">
                                                    Inativa
                                                </span>
                                            )}
                                        </div>
                                        {wallet.description && (
                                            <p className="text-sm text-base-content/60 mb-2">
                                                {wallet.description}
                                            </p>
                                        )}
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="badge badge-sm badge-outline gap-1">
                                                <MdPeople className="text-xs" />
                                                {wallet._count?.members ?? 0} membros
                                            </span>
                                            <span className="badge badge-sm badge-secondary gap-1">
                                                <MdGroups className="text-xs" />
                                                {wallet._count?.clients ?? 0} clientes
                                                {wallet.maxClients != null && ` / ${wallet.maxClients}`}
                                            </span>
                                            {(wallet.companyIds || []).length > 0 && (
                                                <span className="badge badge-sm badge-outline gap-1">
                                                    <MdStore className="text-xs" />
                                                    {wallet.companyIds
                                                        .map(id => {
                                                            const company = companies.find(c => c.id === id)
                                                            return company?.tradingName || company?.name || `#${id}`
                                                        })
                                                        .join(', ')}
                                                </span>
                                            )}
                                            {!wallet.acceptsAutoAssign && (
                                                <span className="badge badge-sm badge-ghost">
                                                    Fora do rodízio
                                                </span>
                                            )}
                                            {wallet.weight > 1 && (
                                                <span className="badge badge-sm badge-ghost">
                                                    Peso {wallet.weight}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            onClick={() => handleSetActive(wallet.id)}
                                            disabled={settingActive}
                                            title={
                                                activeWalletId === wallet.id
                                                    ? 'Deixar de usar como carteira ativa'
                                                    : 'Usar como minha carteira ativa'
                                            }
                                        >
                                            {activeWalletId === wallet.id ? (
                                                <MdStar className="text-lg text-secondary" />
                                            ) : (
                                                <MdStarBorder className="text-lg" />
                                            )}
                                        </button>
                                        {canUpdate && (
                                            <button
                                                className="btn btn-ghost btn-sm"
                                                onClick={() => openEditModal(wallet)}
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                className="btn btn-ghost btn-sm text-error hover:bg-error/10"
                                                onClick={() => handleDelete(wallet)}
                                                title="Excluir"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        )}
                                        <button
                                            className="btn btn-ghost btn-sm"
                                            onClick={() => handleToggleExpand(wallet)}
                                        >
                                            {expandedWalletId === wallet.id ? (
                                                <MdExpandLess className="text-lg" />
                                            ) : (
                                                <MdExpandMore className="text-lg" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded Members Section */}
                                {expandedWalletId === wallet.id && (
                                    <div className="mt-4 pt-4 border-t border-base-300/50">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <h4 className="font-semibold text-base-content flex items-center gap-1">
                                                    <MdPeople className="text-lg" />
                                                    Membros
                                                </h4>
                                                <button
                                                    className="btn btn-sm btn-ghost gap-1"
                                                    onClick={() => openClientsModal(wallet)}
                                                    title="Ver e mover os clientes desta carteira"
                                                >
                                                    <MdGroups className="text-base" />
                                                    Ver clientes ({wallet._count?.clients ?? 0})
                                                </button>
                                            </div>
                                            {canUpdate && (
                                                <button
                                                    className="btn btn-sm btn-outline btn-secondary gap-1"
                                                    onClick={() => setShowAddMemberForm(!showAddMemberForm)}
                                                >
                                                    <MdPersonAdd className="text-base" />
                                                    Adicionar
                                                </button>
                                            )}
                                        </div>

                                        {/* Add Member Form */}
                                        {showAddMemberForm && canUpdate && (
                                            <div className="bg-base-200/50 rounded-lg p-4 mb-4">
                                                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                                                    <div className="flex-1">
                                                        {/* Carteira é da organização inteira: o
                                                            seletor precisa enxergar além da filial ativa. */}
                                                        <UserSelector
                                                            value={memberUserId}
                                                            onChange={userId => setMemberUserId(userId)}
                                                            label="Usuário"
                                                            placeholder="Buscar usuário..."
                                                            scope="organization"
                                                            size="sm"
                                                        />
                                                    </div>
                                                    <div className="flex items-end gap-2">
                                                        <button
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={handleAddMember}
                                                            disabled={addingMember || !memberUserId}
                                                        >
                                                            {addingMember ? (
                                                                <span className="loading loading-spinner loading-xs" />
                                                            ) : (
                                                                'Adicionar'
                                                            )}
                                                        </button>
                                                        <button
                                                            className="btn btn-ghost btn-sm"
                                                            onClick={() => {
                                                                setShowAddMemberForm(false)
                                                                setMemberUserId(null)
                                                            }}
                                                        >
                                                            <MdClose className="text-base" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Members List */}
                                        {loadingDetail ? (
                                            <div className="flex justify-center py-6">
                                                <span className="loading loading-spinner loading-md text-secondary" />
                                            </div>
                                        ) : expandedWallet?.members && expandedWallet.members.length > 0 ? (
                                            <div className="overflow-x-auto">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Usuário</th>
                                                            <th>E-mail</th>
                                                            <th>Status</th>
                                                            {canUpdate && <th className="text-right">Ações</th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {expandedWallet.members.map(member => (
                                                            <tr key={member.id}>
                                                                <td className="font-medium">
                                                                    {member.user?.name || `Usuário #${member.userId}`}
                                                                </td>
                                                                <td className="text-base-content/60">
                                                                    {member.user?.email || '-'}
                                                                </td>
                                                                <td>
                                                                    {canUpdate ? (
                                                                        <input
                                                                            type="checkbox"
                                                                            className="toggle toggle-sm toggle-secondary"
                                                                            checked={member.active}
                                                                            onChange={e =>
                                                                                handleToggleMemberActive(
                                                                                    wallet.id,
                                                                                    member.userId,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <span className={`badge badge-sm ${member.active ? 'badge-success' : 'badge-ghost'}`}>
                                                                            {member.active ? 'Ativo' : 'Inativo'}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                {canUpdate && (
                                                                    <td className="text-right">
                                                                        <button
                                                                            className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                                                                            onClick={() =>
                                                                                handleRemoveMember(
                                                                                    wallet.id,
                                                                                    member.userId,
                                                                                    member.user?.name || `Usuário #${member.userId}`
                                                                                )
                                                                            }
                                                                            title="Remover membro"
                                                                        >
                                                                            <MdPersonRemove className="text-base" />
                                                                        </button>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-6 text-base-content/50 text-sm">
                                                Nenhum membro adicionado a esta carteira
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create / Edit Modal */}
            {showModal && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold">
                                {editingWallet ? 'Editar Carteira' : 'Criar Carteira'}
                            </h3>
                            <button
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => setShowModal(false)}
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>

                        {/* Em massa só faz sentido na criação. */}
                        {!editingWallet && (
                            <div role="tablist" className="tabs tabs-bordered mb-5">
                                <button
                                    role="tab"
                                    className={`tab ${modalTab === 'single' ? 'tab-active' : ''}`}
                                    onClick={() => setModalTab('single')}
                                >
                                    Carteira avulsa
                                </button>
                                <button
                                    role="tab"
                                    className={`tab ${modalTab === 'bulk' ? 'tab-active' : ''}`}
                                    onClick={() => {
                                        setModalTab('bulk')
                                        if (eligibleUsers.length === 0) loadEligibleUsers()
                                    }}
                                >
                                    Uma por vendedor
                                </button>
                            </div>
                        )}

                        {modalTab === 'bulk' && !editingWallet && (
                            <div className="space-y-4">
                                <p className="text-xs text-base-content/60 leading-relaxed">
                                    Cada marcado ganha uma carteira com o nome dele, já como membro
                                    e com as filiais dele — salvo se você escolher filiais abaixo,
                                    que então valem para todas.
                                </p>

                                <input
                                    type="text"
                                    className="input input-bordered input-sm w-full"
                                    placeholder="Buscar por nome, e-mail ou filial..."
                                    value={userSearch}
                                    onChange={e => setUserSearch(e.target.value)}
                                />

                                {loadingEligible ? (
                                    <div className="flex justify-center py-8">
                                        <span className="loading loading-spinner loading-md text-secondary" />
                                    </div>
                                ) : filteredEligibleUsers.length === 0 ? (
                                    <div className="text-center py-8 text-sm text-base-content/50">
                                        Nenhum usuário encontrado
                                    </div>
                                ) : (
                                    <div className="border border-base-300 rounded-lg max-h-72 overflow-y-auto">
                                        <table className="table table-sm">
                                            <thead className="sticky top-0 bg-base-100 z-10">
                                                <tr>
                                                    <th className="w-10">
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox checkbox-sm checkbox-secondary"
                                                            checked={
                                                                selectableUsers.length > 0 &&
                                                                selectedUserIds.length === selectableUsers.length
                                                            }
                                                            onChange={toggleAllUsers}
                                                            disabled={selectableUsers.length === 0}
                                                        />
                                                    </th>
                                                    <th>Usuário</th>
                                                    <th>Filiais</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredEligibleUsers.map(u => (
                                                    <tr
                                                        key={u.id}
                                                        className={u.hasWallet ? 'opacity-50' : 'cursor-pointer'}
                                                        onClick={() => !u.hasWallet && toggleUser(u.id)}
                                                    >
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                className="checkbox checkbox-sm checkbox-secondary"
                                                                checked={selectedUserIds.includes(u.id)}
                                                                onChange={() => toggleUser(u.id)}
                                                                disabled={u.hasWallet}
                                                            />
                                                        </td>
                                                        <td>
                                                            <div className="font-medium">{u.name}</div>
                                                            {u.email && (
                                                                <div className="text-xs text-base-content/60">
                                                                    {u.email}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {u.hasWallet ? (
                                                                <span className="badge badge-sm badge-ghost">
                                                                    Já tem carteira
                                                                </span>
                                                            ) : u.companyNames.length > 0 ? (
                                                                <span className="text-xs text-base-content/70">
                                                                    {u.companyNames.join(', ')}
                                                                </span>
                                                            ) : (
                                                                <span className="badge badge-sm badge-warning badge-outline">
                                                                    Sem filial
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={`space-y-5 ${modalTab === 'bulk' && !editingWallet ? 'mt-5' : ''}`}>
                            {modalTab === 'single' && (
                                <>
                                    <div className="form-control w-full">
                                        <label className="label pb-1">
                                            <span className="label-text font-medium">Nome *</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            placeholder="Ex: Carteira Premium"
                                            value={formName}
                                            onChange={e => setFormName(e.target.value)}
                                            autoFocus
                                        />
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label pb-1">
                                            <span className="label-text font-medium">Descrição</span>
                                        </label>
                                        <textarea
                                            className="textarea textarea-bordered w-full"
                                            placeholder="Descrição da carteira..."
                                            rows={3}
                                            value={formDescription}
                                            onChange={e => setFormDescription(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="divider my-1 text-xs text-base-content/50">
                                Distribuição automática
                            </div>

                            {companies.length > 1 && (
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Filiais atendidas
                                        </span>
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {companies.map(company => {
                                            const selected = formCompanyIds.includes(company.id)
                                            return (
                                                <button
                                                    type="button"
                                                    key={company.id}
                                                    className={`btn btn-sm ${selected ? 'btn-secondary' : 'btn-outline'}`}
                                                    onClick={() => toggleFormCompany(company.id)}
                                                >
                                                    {company.tradingName || company.name}
                                                </button>
                                            )
                                        })}
                                    </div>
                                    <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        Clientes novos dessas filiais são roteados para cá. Nenhuma selecionada = atende todas. A carteira continua valendo em qualquer cidade — isto governa só o roteamento.
                                    </p>
                                </div>
                            )}

                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3 py-1">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={formAcceptsAutoAssign}
                                        onChange={e => setFormAcceptsAutoAssign(e.target.checked)}
                                    />
                                    <span className="label-text font-medium">
                                        Participa do rodízio de clientes novos
                                    </span>
                                </label>
                                <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        Desligado, a carteira só recebe clientes por cadastro direto ou transferência.
                                    </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">Peso no rodízio</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="input input-bordered w-full"
                                        min={1}
                                        value={formWeight}
                                        onChange={e => setFormWeight(e.target.value)}
                                    />
                                    <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        Peso maior recebe com mais frequência.
                                    </p>
                                </div>

                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">Limite de clientes</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="input input-bordered w-full"
                                        placeholder="Sem limite"
                                        min={1}
                                        value={formMaxClients}
                                        onChange={e => setFormMaxClients(e.target.value)}
                                    />
                                    <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        Ao atingir o limite, a carteira sai do rodízio.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setShowModal(false)}
                                disabled={submitting}
                            >
                                Cancelar
                            </button>
                            {modalTab === 'bulk' && !editingWallet ? (
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleCreateBulk}
                                    disabled={creatingBulk || selectedUserIds.length === 0}
                                >
                                    {creatingBulk ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        `Criar ${selectedUserIds.length || ''} carteira${selectedUserIds.length === 1 ? '' : 's'}`
                                    )}
                                </button>
                            ) : (
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleSubmit}
                                    disabled={submitting || !formName.trim()}
                                >
                                    {submitting ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : editingWallet ? (
                                        'Salvar'
                                    ) : (
                                        'Criar'
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setShowModal(false)}>close</button>
                    </form>
                </dialog>
            )}

            {/* Config Modal */}
            {showConfigModal && config && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">Distribuição automática</h3>
                            <button
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => setShowConfigModal(false)}
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3 py-1">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary"
                                        checked={config.autoAssignEnabled}
                                        onChange={e =>
                                            setConfig({ ...config, autoAssignEnabled: e.target.checked })
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Distribuir clientes novos automaticamente
                                    </span>
                                </label>
                                <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        Vale para quem chega por integração, IA ou loja — sem usuário que cadastrou.
                                    </p>
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">Critério do rodízio</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={config.distributionMode}
                                    onChange={e =>
                                        setConfig({
                                            ...config,
                                            distributionMode: e.target.value as WalletDistributionMode,
                                        })
                                    }
                                >
                                    {(Object.keys(WALLET_DISTRIBUTION_MODE_LABELS) as WalletDistributionMode[]).map(mode => (
                                        <option key={mode} value={mode}>
                                            {WALLET_DISTRIBUTION_MODE_LABELS[mode]}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        {WALLET_DISTRIBUTION_MODE_HINTS[config.distributionMode]}
                                    </p>
                            </div>

                            <div className="form-control w-full">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">Carteira padrão</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={config.defaultWalletId ?? ''}
                                    onChange={e =>
                                        setConfig({
                                            ...config,
                                            defaultWalletId: e.target.value
                                                ? parseInt(e.target.value, 10)
                                                : null,
                                        })
                                    }
                                >
                                    <option value="">Nenhuma (cliente fica no pool)</option>
                                    {wallets.map(w => (
                                        <option key={w.id} value={w.id}>
                                            {w.name}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        Usada quando nenhuma carteira é elegível para o cliente.
                                    </p>
                            </div>

                            <div className="divider my-1" />

                            <div className="form-control w-full">
                                <label className="label cursor-pointer justify-start gap-3 py-1">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-warning"
                                        checked={config.prospectDiscardEnabled}
                                        onChange={e =>
                                            setConfig({
                                                ...config,
                                                prospectDiscardEnabled: e.target.checked,
                                            })
                                        }
                                    />
                                    <span className="label-text font-medium">
                                        Descartar contatos mortos automaticamente
                                    </span>
                                </label>
                                <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                    Todo dia, apaga contatos que nunca foram identificados
                                    (sem CPF, sem data de nascimento, sem e-mail) e nunca
                                    tiveram agendamento, cobrança ou pedido. Cadastro com
                                    qualquer um desses dados nunca é tocado.
                                </p>
                            </div>

                            {config.prospectDiscardEnabled && (
                                <div className="form-control w-full">
                                    <label className="label pb-1">
                                        <span className="label-text font-medium">
                                            Dias sem atividade antes do descarte
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        min={30}
                                        className="input input-bordered w-full"
                                        placeholder="365"
                                        value={config.prospectRetentionDays ?? ''}
                                        onChange={e =>
                                            setConfig({
                                                ...config,
                                                prospectRetentionDays: e.target.value
                                                    ? parseInt(e.target.value, 10)
                                                    : null,
                                            })
                                        }
                                    />
                                    <p className="text-xs text-base-content/50 mt-1 leading-relaxed">
                                        Vazio usa o padrão de 365 dias. Mínimo de 30.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => setShowConfigModal(false)}
                                disabled={savingConfig}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleSaveConfig}
                                disabled={savingConfig}
                            >
                                {savingConfig ? (
                                    <span className="loading loading-spinner loading-sm" />
                                ) : (
                                    'Salvar'
                                )}
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setShowConfigModal(false)}>close</button>
                    </form>
                </dialog>
            )}

            {/* Clientes da carteira */}
            {clientsModalWallet && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box !max-w-4xl">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-bold">
                                    Clientes de {clientsModalWallet.name}
                                </h3>
                                <p className="text-sm text-base-content/60">
                                    {clientsTotal} cliente(s) nesta carteira
                                </p>
                            </div>
                            <button
                                className="btn btn-ghost btn-sm btn-circle"
                                onClick={() => setClientsModalWallet(null)}
                            >
                                <MdClose className="text-lg" />
                            </button>
                        </div>

                        <input
                            type="text"
                            className="input input-bordered input-sm w-full mb-3"
                            placeholder="Buscar por nome, e-mail, telefone ou CPF..."
                            value={clientsSearch}
                            onChange={e => {
                                setClientsSearch(e.target.value)
                                setClientsPage(1)
                            }}
                            autoFocus
                        />

                        {/* Barra de transferência: só aparece com alguém marcado. */}
                        {canUpdate && selectedClientIds.length > 0 && (
                            <div className="flex flex-col sm:flex-row sm:items-end gap-3 bg-base-200/50 rounded-lg p-3 mb-3">
                                <div className="flex-1">
                                    <WalletSelector
                                        value={targetWalletId}
                                        onChange={walletId => setTargetWalletId(walletId)}
                                        label={`Mover ${selectedClientIds.length} cliente(s) para`}
                                        excludeIds={[clientsModalWallet.id]}
                                        size="sm"
                                    />
                                </div>
                                <button
                                    className="btn btn-secondary btn-sm gap-2"
                                    onClick={handleMoveClients}
                                    disabled={movingClients || !targetWalletId}
                                >
                                    {movingClients ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdMoveDown className="text-base" />
                                    )}
                                    Mover
                                </button>
                            </div>
                        )}

                        {canUpdate && selectedClientIds.length > 0 && (
                            <label
                                className="label cursor-pointer justify-start gap-3 py-0 mb-3"
                                htmlFor="wallets-move-deals"
                            >
                                <input
                                    id="wallets-move-deals"
                                    type="checkbox"
                                    className="checkbox checkbox-secondary checkbox-xs"
                                    checked={moveClientDeals}
                                    onChange={e => setMoveClientDeals(e.target.checked)}
                                    disabled={movingClients}
                                />
                                <span className="label-text text-xs">
                                    Levar as oportunidades abertas para o funil da nova dona
                                </span>
                            </label>
                        )}

                        {loadingClients ? (
                            <div className="flex justify-center py-12">
                                <span className="loading loading-spinner loading-md text-secondary" />
                            </div>
                        ) : walletClients.length === 0 ? (
                            <div className="text-center py-12 text-sm text-base-content/50">
                                {clientsSearch
                                    ? 'Nenhum cliente encontrado para esta busca'
                                    : 'Esta carteira ainda não tem clientes'}
                            </div>
                        ) : (
                            <div className="border border-base-300 rounded-lg max-h-96 overflow-y-auto">
                                <table className="table table-sm table-pin-rows">
                                    <thead>
                                        <tr>
                                            {canUpdate && (
                                                <th className="w-10">
                                                    <input
                                                        type="checkbox"
                                                        className="checkbox checkbox-sm checkbox-secondary"
                                                        checked={
                                                            walletClients.length > 0 &&
                                                            selectedClientIds.length === walletClients.length
                                                        }
                                                        onChange={toggleAllClients}
                                                    />
                                                </th>
                                            )}
                                            <th>Cliente</th>
                                            <th>Contato</th>
                                            <th>Cidade</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {walletClients.map(client => (
                                            <tr
                                                key={client.id}
                                                className="cursor-pointer hover"
                                                onClick={() => canUpdate && toggleClient(client.id)}
                                            >
                                                {canUpdate && (
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox checkbox-sm checkbox-secondary"
                                                            checked={selectedClientIds.includes(client.id)}
                                                            onChange={() => toggleClient(client.id)}
                                                        />
                                                    </td>
                                                )}
                                                <td>
                                                    <div className="font-medium">{client.name}</div>
                                                    {client.cpf && (
                                                        <div className="text-xs text-base-content/60">
                                                            {client.cpf}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-base-content/70 text-xs">
                                                    <div>
                                                        {client.whatsAppNumber
                                                            ? formatPhoneNumber(client.whatsAppNumber)
                                                            : '-'}
                                                    </div>
                                                    {client.email && <div>{client.email}</div>}
                                                </td>
                                                <td className="text-base-content/70">
                                                    {client.city || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {clientsTotalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-3">
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() => setClientsPage(p => Math.max(1, p - 1))}
                                    disabled={clientsPage === 1}
                                >
                                    Anterior
                                </button>
                                <span className="text-sm text-base-content/60">
                                    Página {clientsPage} de {clientsTotalPages}
                                </span>
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() => setClientsPage(p => Math.min(clientsTotalPages, p + 1))}
                                    disabled={clientsPage === clientsTotalPages}
                                >
                                    Próxima
                                </button>
                            </div>
                        )}
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setClientsModalWallet(null)}>close</button>
                    </form>
                </dialog>
            )}

            {/* Delete Wallet Confirmation */}
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setWalletToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir carteira"
                message={`Tem certeza de que deseja excluir a carteira "${walletToDelete?.name}"? Os clientes dela voltam para o pool "Sem carteira". Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            {/* Remove Member Confirmation */}
            <ConfirmModal
                isOpen={showRemoveMemberModal}
                onClose={() => {
                    setShowRemoveMemberModal(false)
                    setMemberToRemove(null)
                }}
                onConfirm={confirmRemoveMember}
                title="Remover membro"
                message={`Tem certeza de que deseja remover "${memberToRemove?.userName}" desta carteira? Os clientes permanecem na carteira.`}
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
                loading={removingMember}
            />
        </div>
    )
}
